import { createMeal } from '@/utilities/factories'
import { SakaraClassicMeal } from './sakara-classic-meal'
import dayjs, { type Dayjs } from '@/utilities/dayjs-config'
import type { Meal as PopsMeal } from '@/types/pops'
import type { Meal } from './meal'

// Ordering of meals within a day
const MEAL_ORDER = ['breakfast', 'lunch', 'dinner']

export type GroupedMenu = {
  date: Dayjs
  meals: Meal[]
}

export class Menu {
  menu: Meal[]
  sakaraClassicMenu: SakaraClassicMeal[]
  menuGroupedByDate: GroupedMenu[]
  week: dayjs.Dayjs

  constructor(week: Dayjs, rawMenu: PopsMeal[]) {
    this.menu = []
    this.sakaraClassicMenu = []

    rawMenu.forEach((rawMeal) => {
      const meal = createMeal(rawMeal)

      if (meal instanceof SakaraClassicMeal) {
        this.sakaraClassicMenu.push(meal)
      } else {
        this.menu.push(meal)
      }
    })

    this.menu = this.menu.sort(
      (mealA, mealB) =>
        // Orders meals by date and by meal for each date
        mealA.date.unix() +
        MEAL_ORDER.indexOf(mealA.mealType) -
        (mealB.date.unix() + MEAL_ORDER.indexOf(mealB.mealType)),
    )

    this.menuGroupedByDate = Menu.groupMenuByDate(this.menu)
    this.week = dayjs(week)
  }

  getMealByMealCode(mealCode: string) {
    let foundMeal = this.menu.find((meal) => meal.mealCode === mealCode)

    if (!foundMeal) {
      foundMeal = this.sakaraClassicMenu.find(
        (meal) => meal.mealCode === mealCode,
      )
    }

    return foundMeal
  }

  getMealsByMealCodes(mealCodes: string[]) {
    return mealCodes.map((mealCode) => this.getMealByMealCode(mealCode))
  }

  getSakaraClassicMeals(deliveryCycle: number) {
    return this.sakaraClassicMenu.filter(
      (meal) => meal.classicMealProperty.deliveryCycle === deliveryCycle,
    )
  }

  /**
   * Gets the menu grouped by date. Each date with its own meals array.
   *
   * @example
   * [
   * 	{
   * 		"date": dayjs(),
   * 		"meals": [Meal, Meal, Meal]
   * 	},
   * 	{
   * 		"date": dayjs(),
   * 		"meals": [Meal, Meal, Meal]
   * 	},
   * 	...
   * ]
   */
  static groupMenuByDate(menu: Meal[]) {
    return menu.reduce((groupedMenu, meal) => {
      const dayMenu = groupedMenu[groupedMenu.length - 1]

      if (dayMenu && dayMenu.date.isSame(meal.date, 'day')) {
        dayMenu.meals.push(meal)
      } else {
        groupedMenu.push({
          date: meal.date,
          meals: [meal],
        })
      }

      return groupedMenu
    }, [] as GroupedMenu[])
  }
}
